/**
 * Telegram WebApp entegrasyonu — initData, theme, BackButton, MainButton.
 */

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name?: string; last_name?: string; username?: string; language_code?: string; photo_url?: string };
    auth_date?: number;
    hash?: string;
  };
  colorScheme: 'light' | 'dark';
  themeParams: {
    bg_color?: string;
    secondary_bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
  };
  version: string;
  platform: string;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  enableClosingConfirmation?(): void;
  disableVerticalSwipes?(): void;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  MainButton: {
    text: string;
    show(): void; hide(): void;
    setText(t: string): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    color: string; textColor: string;
    setParams(p: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }): void;
    showProgress?(leaveActive?: boolean): void;
    hideProgress?(): void;
  };
  HapticFeedback?: {
    impactOccurred(s: 'light'|'medium'|'heavy'|'rigid'|'soft'): void;
    notificationOccurred(t: 'error'|'success'|'warning'): void;
    selectionChanged(): void;
  };
}

export function tg(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function getInitData(): string {
  return tg()?.initData ?? '';
}

/**
 * Stale initData icin tek seferlik refresh: WebApp ready() cagrilarak
 * Telegram client'tan guncel initData alinir. 401 retry'larinda kullanilir.
 */
export function refreshInitData(): string {
  try { tg()?.ready(); } catch { /* sessiz */ }
  return tg()?.initData ?? '';
}

export function getTelegramUser() {
  return tg()?.initDataUnsafe?.user ?? null;
}

/**
 * Telegram'ın gönderdiği theme renklerini Algomed semantik token'larıyla
 * uyumlu olacak şekilde uygula. Body bg ve header rengi senkron.
 */
export function applyTelegramTheme() {
  const w = tg();
  if (!w) return;
  const tp = w.themeParams || {};
  const root = document.documentElement;
  if (tp.bg_color) root.style.setProperty('--tg-bg', tp.bg_color);
  if (tp.text_color) root.style.setProperty('--tg-text', tp.text_color);
  if (tp.hint_color) root.style.setProperty('--tg-hint', tp.hint_color);
  if (tp.button_color) root.style.setProperty('--tg-accent', tp.button_color);
  // Algomed brand-blue ile header
  try { w.setHeaderColor?.('#4DA8FF'); } catch {}
  try { w.setBackgroundColor?.(tp.bg_color || (w.colorScheme === 'dark' ? '#1a1d29' : '#F8FBFF')); } catch {}
}

export function initTelegram() {
  const w = tg();
  if (!w) return;
  w.ready();
  w.expand();
  applyTelegramTheme();
  try { w.disableVerticalSwipes?.(); } catch {}
}

if (typeof window !== 'undefined') initTelegram();

/** React hook-friendly BackButton helper. */
export function useTelegramBackButton(onBack: () => void) {
  const w = tg();
  if (!w) return () => {};
  w.BackButton.show();
  w.BackButton.onClick(onBack);
  return () => {
    w.BackButton.offClick(onBack);
    w.BackButton.hide();
  };
}

/** MainButton helper. */
export function setMainButton(text: string, onClick: () => void, opts?: { color?: string; loading?: boolean }) {
  const w = tg();
  if (!w) return () => {};
  w.MainButton.setParams({
    text,
    color: opts?.color || '#4DA8FF',
    text_color: '#ffffff',
    is_active: true,
    is_visible: true,
  });
  w.MainButton.onClick(onClick);
  if (opts?.loading) w.MainButton.showProgress?.(true);
  return () => {
    w.MainButton.offClick(onClick);
    w.MainButton.hideProgress?.();
    w.MainButton.hide();
  };
}

export function haptic(type: 'success' | 'error' | 'warning' | 'select' = 'success') {
  const h = tg()?.HapticFeedback;
  if (!h) return;
  if (type === 'select') h.selectionChanged();
  else h.notificationOccurred(type);
}
